'use client'

import { useState } from 'react'
import { X, ChevronLeft } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'

// 대분류 타입
type RatingType = 'bad' | 'good' | 'great' | null

// 체크박스 항목 정의
const REVIEW_ITEMS = {
  bad: [
    { id: 'no_time', label: '시간 약속을 안 지켰어요', change: -0.05 },
    { id: 'no_reply', label: '채팅 메시지를 읽고도 답이 없어요', change: -0.05 },
    { id: 'no_show', label: '약속 장소에 나타나지 않았어요', change: -0.05 },
    { id: 'unsafe', label: '안전 수칙을 지키지 않았어요', change: -0.05 },
    { id: 'rude', label: '불친절해요', change: -0.05 },
    { id: 'cancelled', label: '갑자기 취소했어요', change: -0.05 },
  ],
  good: [
    { id: 'punctual', label: '시간 약속을 잘 지켜요', change: 0.07 },
    { id: 'kind', label: '친절하고 매너가 좋아요', change: 0.07 },
    { id: 'fast_reply', label: '응답이 빨라요', change: 0.07 },
    { id: 'safe', label: '안전하게 다이빙했어요', change: 0.07 },
  ],
  great: [
    { id: 'punctual', label: '시간 약속을 잘 지켜요', change: 0.10 },
    { id: 'kind', label: '친절하고 매너가 좋아요', change: 0.10 },
    { id: 'fast_reply', label: '응답이 빨라요', change: 0.10 },
    { id: 'safe', label: '안전하게 다이빙했어요', change: 0.10 },
  ],
}

// 대분류 옵션
const RATING_OPTIONS = [
  { type: 'bad' as RatingType, emoji: '😞', label: '별로예요', color: 'text-gray-400', selectedBg: 'bg-gray-100 dark:bg-gray-700', selectedBorder: 'border-gray-400' },
  { type: 'good' as RatingType, emoji: '🙂', label: '좋아요', color: 'text-blue-500', selectedBg: 'bg-blue-50 dark:bg-blue-900/30', selectedBorder: 'border-blue-500' },
  { type: 'great' as RatingType, emoji: '😊', label: '최고예요', color: 'text-orange-500', selectedBg: 'bg-orange-50 dark:bg-orange-900/30', selectedBorder: 'border-orange-500' },
]

interface BuddyReviewModalV2Props {
  isOpen: boolean
  onClose: () => void
  matchInfo: {
    matchRequestId: string
    date: string
    location: string
    buddyNames: string[] // 평가할 버디들 이름
    buddyIds: string[]   // 평가할 버디들 ID
  }
  onSubmit: (data: ReviewSubmitData) => Promise<void>
}

export interface ReviewSubmitData {
  matchRequestId: string
  reviewedUserIds: string[]  // 평가 대상들 (그룹 매칭 시 여러명)
  ratingType: RatingType
  selectedItems: string[]
  temperatureChange: number
}

export default function BuddyReviewModalV2({
  isOpen,
  onClose,
  matchInfo,
  onSubmit
}: BuddyReviewModalV2Props) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const [ratingType, setRatingType] = useState<RatingType>(null)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  // 대분류 선택 시 체크박스 초기화
  const handleRatingSelect = (type: RatingType) => {
    setRatingType(type)
    setSelectedItems([])
  }

  // 체크박스 토글
  const toggleItem = (itemId: string) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  // 온도 변화량 계산
  const calculateTemperatureChange = (): number => {
    if (!ratingType || selectedItems.length === 0) return 0

    const items = REVIEW_ITEMS[ratingType]
    let total = 0

    selectedItems.forEach(itemId => {
      const item = items.find(i => i.id === itemId)
      if (item) {
        total += item.change
      }
    })

    return Math.round(total * 100) / 100 // 소수점 2자리
  }

  // 제출 가능 여부
  const canSubmit = ratingType !== null && selectedItems.length > 0

  // 제출
  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return

    setIsSubmitting(true)
    try {
      const temperatureChange = calculateTemperatureChange()

      await onSubmit({
        matchRequestId: matchInfo.matchRequestId,
        reviewedUserIds: matchInfo.buddyIds,
        ratingType,
        selectedItems,
        temperatureChange
      })

      onClose()
    } catch (error) {
      console.error('평가 제출 실패:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // 현재 체크박스 목록
  const currentItems = ratingType ? REVIEW_ITEMS[ratingType] : []

  // 질문 텍스트
  const getQuestionText = () => {
    if (ratingType === 'bad') return '어떤 점이 별로였나요?'
    return '어떤 점이 좋았나요?'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 모달 컨텐츠 */}
      <div className={`relative w-full max-w-md mx-4 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* 헤더 */}
        <div className={`flex items-center justify-between p-4 border-b shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            매칭 평가
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 매칭 정보 */}
        <div className={`p-4 border-b shrink-0 ${
          isDark ? 'border-gray-700 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
        }`}>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {matchInfo.date} · {matchInfo.location}
          </p>
          <p className={`font-medium mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            함께한 버디: {matchInfo.buddyNames.join(', ')}
          </p>
        </div>

        {/* 스크롤 영역 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 메인 질문 */}
          <div className="text-center mb-6">
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              오늘 다이빙은 어떠셨나요?
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              평가는 본인만 볼 수 있어요
            </p>
          </div>

          {/* 대분류 선택 */}
          <div className="flex justify-center gap-4 mb-8">
            {RATING_OPTIONS.map(option => (
              <button
                key={option.type}
                onClick={() => handleRatingSelect(option.type)}
                className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all min-w-[90px] ${
                  ratingType === option.type
                    ? `${option.selectedBg} ${option.selectedBorder}`
                    : isDark
                      ? 'border-gray-600 hover:border-gray-500'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-2 ${
                  ratingType === option.type
                    ? option.selectedBg
                    : isDark ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  {option.emoji}
                </div>
                <span className={`text-sm font-medium ${
                  ratingType === option.type
                    ? option.color
                    : isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {option.label}
                </span>
              </button>
            ))}
          </div>

          {/* 체크박스 영역 */}
          {ratingType && (
            <div className="space-y-4">
              <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {getQuestionText()}
              </h4>

              <div className="space-y-2">
                {currentItems.map(item => (
                  <label
                    key={item.id}
                    className={`flex items-center p-3 rounded-xl cursor-pointer transition-colors ${
                      selectedItems.includes(item.id)
                        ? isDark
                          ? 'bg-blue-900/30 border border-blue-500'
                          : 'bg-blue-50 border border-blue-500'
                        : isDark
                          ? 'bg-gray-700 border border-gray-600 hover:bg-gray-600'
                          : 'bg-gray-100 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItem(item.id)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center transition-colors ${
                      selectedItems.includes(item.id)
                        ? 'bg-blue-500 border-blue-500'
                        : isDark
                          ? 'border-gray-500'
                          : 'border-gray-300'
                    }`}>
                      {selectedItems.includes(item.id) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>

              {/* 온도 변화 미리보기 */}
              {selectedItems.length > 0 && (
                <div className={`mt-4 p-3 rounded-lg text-center ${
                  isDark ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    버디 온도 변화:
                  </span>
                  <span className={`ml-2 font-bold ${
                    calculateTemperatureChange() >= 0 ? 'text-orange-500' : 'text-blue-500'
                  }`}>
                    {calculateTemperatureChange() >= 0 ? '+' : ''}{calculateTemperatureChange().toFixed(1)}°C
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className={`p-4 border-t shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            className={`w-full py-3 rounded-xl font-medium transition-colors ${
              canSubmit && !isSubmitting
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : isDark
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? '제출 중...' : '평가 완료'}
          </button>
        </div>
      </div>
    </div>
  )
}
